import { useEffect } from 'react';
import { driver } from 'driver.js';
// @ts-ignore — CSS side-effect import, not a TS module
import 'driver.js/dist/driver.css';
import { useAuthStore } from '@/store/authStore';
import { authApi } from '@/services/api';

export function useGuidedTour() {
  const { user, updateUser } = useAuthStore();

  useEffect(() => {
    if (!user) return;
    
    // Only show if user's tour version is less than the current system version
    const tourVersion = user.tourVersion || 0;
    const currentTourVersion = user.currentTourVersion || 1;

    if (tourVersion >= currentTourVersion) return;

    const timer = setTimeout(() => {
      const d = driver({
        showProgress: true,
        animate: true,
        allowClose: true,
        showButtons: ['next'],
        doneBtnText: 'Finish',
        nextBtnText: 'Next',
        onDestroyStarted: () => {
          d.destroy();
          // Update version in backend and local store
          authApi.updateTourVersion(currentTourVersion).catch(console.error);
          updateUser({ tourVersion: currentTourVersion });
        },
        onPopoverRender: (popover, { driver }) => {
          if (driver.hasNextStep()) {
            if (!popover.footerButtons.querySelector('.driver-skip-btn')) {
              const skipBtn = document.createElement('button');
              skipBtn.className = 'driver-skip-btn';
              skipBtn.innerText = 'Skip Tour';
              skipBtn.style.padding = '5px 10px';
              skipBtn.style.marginRight = '10px';
              skipBtn.style.border = '1px solid #d1d5db';
              skipBtn.style.borderRadius = '6px';
              skipBtn.style.background = '#f9fafb';
              skipBtn.style.cursor = 'pointer';
              skipBtn.style.fontSize = '14px';
              skipBtn.style.color = '#374151';
              skipBtn.style.fontWeight = '500';
              skipBtn.onclick = () => driver.destroy();
              popover.footerButtons.insertBefore(skipBtn, popover.nextButton);
            }
          }
        },
        steps: getStepsForRole(user.role).filter(step => document.querySelector(step.element))
      });

      d.drive();
    }, 2000);

    return () => clearTimeout(timer);
  }, [user, updateUser]);

  const getStepsForRole = (role: string) => {
    const baseSteps = [
      {
        element: '#tour-notifications',
        popover: { title: 'Notifications', description: 'Check here for updates on your leave requests and approvals.' }
      }
    ];

    const employeeSteps = [
      {
        element: '#tour-balances',
        popover: { title: 'Leave Balances', description: 'Your current leave balances and entitlements are shown here.' }
      },
      {
        element: '#tour-apply',
        popover: { title: 'Apply for Leave', description: 'Click here to submit a new leave application.' }
      },
      {
        element: '#tour-nav-my-leaves',
        popover: { title: 'My Leaves', description: 'View your pending, approved, and past leave history.' }
      }
    ];

    switch (role) {
      case 'MANAGER':
      case 'SENIOR_MANAGER':
      case 'HR_MANAGER':
        return [
          ...employeeSteps,
          {
            element: '#tour-nav-inbox',
            popover: { title: 'Approval Inbox', description: 'Review and approve/reject leave requests from your team here.' }
          },
          {
            element: '#tour-nav-team',
            popover: { title: 'Team Calendar', description: 'View upcoming leaves and plan team coverage.' }
          },
          ...baseSteps
        ];
      case 'MD':
        return [
          {
            element: '#tour-nav-inbox',
            popover: { title: 'Approval Inbox', description: 'Review and approve/reject leave requests from your team here.' }
          },
          {
            element: '#tour-nav-team',
            popover: { title: 'Team Calendar', description: 'View upcoming leaves and plan team coverage.' }
          },
          {
            element: '#tour-md-summary',
            popover: { title: 'Executive Summary', description: 'Overview of organization-wide attendance and pending items.' }
          },
          {
            element: '#tour-md-heatmap',
            popover: { title: 'Leave Density Heatmap', description: 'Identify peak leave periods across the company.' }
          },
          ...baseSteps
        ];
      case 'ORG_ADMIN':
        return [
          {
            element: '#tour-admin-settings',
            popover: { title: 'Admin Controls', description: 'Manage users, policies, hierarchy, and system settings from this section.' }
          },
          ...baseSteps
        ];
      default:
        return [...employeeSteps, ...baseSteps];
    }
  };
}
